import {useState, type ReactNode} from "react";
import cn from "classnames";

import {ALL_PRODUCT, RELEASES_SETS} from "data/constants";
import {findChannelObj, isContentTypeSelectable} from "data/products";
import {releasesSetsForProduct} from "data/products/index";
import {applyStageOrder} from "libs/stageOrder";
import {
  ALL_PRODUCTS_SCOPE,
  hiddenSetsOf,
  isOnStageInCode,
  isSetHiddenAbove,
  stageHasProduct,
  type ChannelSettingsLike,
  type HiddenChange,
} from "libs/selectorVisibility";
import CheckboxGroup from "./CheckboxGroup";
import type {OptionCheckbox} from "./LayoutVisibilityGroup";

/**
 * The products and the sets that the selectors offer, per stage. An admin
 * clears a checkbox and the option leaves the selector of that stage only.
 * The "All stages" column (products) and row (sets) are the channel-wide
 * lists. A product hidden there is hidden on each stage, so its stage boxes
 * show cleared and disabled. A link still opens a hidden option.
 *
 * Products: one row per product, one column per stage of the channel. The
 * "Every product" row hides the stage itself. On a stage-typed channel
 * (Releases) each product has a box on each stage: a box of a stage that the
 * code does not give the product puts the product on that stage. On the other
 * channels a product has no box on a type that the code does not give it. A
 * box that differs from the code has a frame.
 *
 * Sets (releases): one row per stage, one column per set, for the page's
 * product or for every product. Here the narrowest rule wins: a stage box can
 * show a set that the "All stages" row hides, and a box of one product can
 * show or hide a set against the stage for every product. A box with a rule
 * of its own has a frame. A click that gives the state of the rule above
 * removes the rule of the box.
 */

interface Option {
  label: string;
  value: string;
}

interface Cell {
  key: string;
  isHidden: boolean;
  /** The cell has a rule of its own: it differs from the rule above it. */
  isOwn?: boolean;
  input: ReactNode;
}

/** The selectable stages of a channel, in the saved Stage order. */
export function channelStages(channelValue: string, stageOrder: string[] | null | undefined): Option[] {
  const types: Option[] = (findChannelObj(channelValue)?.types ?? [])
    .filter((type: Option) => isContentTypeSelectable(type.value))
    .map(({label, value}: Option) => ({label, value}));
  return applyStageOrder(types, stageOrder);
}

/** A grid of checkboxes: a head row, then the rows; null cells are empty. */
function Matrix({
  corner,
  columns,
  rows,
}: {
  corner?: ReactNode;
  columns: Array<{key: string; label: string; isCurrent?: boolean; title?: string}>;
  rows: Array<{key: string; label: ReactNode; isCurrent?: boolean; cells: Array<Cell | null>}>;
}) {
  return (
    <div
      className="stage-visibility-matrix"
      style={{gridTemplateColumns: `minmax(64px, auto) repeat(${columns.length}, minmax(22px, 1fr))`}}
    >
      <span>{corner}</span>
      {columns.map((column) => (
        <span
          key={column.key}
          className={cn("bg-set-head", {"is-current": column.isCurrent})}
          title={column.title ?? column.label}
        >
          {column.label}
        </span>
      ))}
      {rows.map((row) => (
        <div key={row.key} className="layout-visibility-row">
          <span className={cn("layout-visibility-stage", {"is-current": row.isCurrent})}>
            {row.label}
          </span>
          {row.cells.map((cell, index) => (
            <span
              key={cell?.key ?? `empty-${index}`}
              className={cn("layout-visibility-cell", {
                "is-hidden": cell?.isHidden,
                "is-own": cell?.isOwn,
              })}
            >
              {cell?.input}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

/** The title of a box of a stage that the code does not give the product. */
function addedBoxTitle(product: string, stage: string, isOff: boolean): string {
  return isOff
    ? `${product} is not on ${stage} in the code. Click to add it`
    : `${product} is on ${stage} by an admin rule, not by the code`;
}

export function ProductStageVisibilityGroup({
  channel,
  products,
  stages,
  currentType,
  settings,
  checkbox,
}: {
  channel: Option;
  /** The products of the channel's Product dropdown, "All Products" included. */
  products: Option[];
  stages: Option[];
  currentType: string | undefined;
  settings: ChannelSettingsLike;
  checkbox: OptionCheckbox;
}) {
  const hiddenEverywhere = new Set(settings?.hiddenProducts ?? []);
  const stageLists = settings?.hiddenStageProducts ?? {};
  const isHiddenOn = (stage: string, value: string) => (stageLists[stage] ?? []).includes(value);
  const productChange = (value: string, type?: string): Omit<HiddenChange, "hidden"> => ({
    channel: channel.value,
    kind: "product",
    value,
    ...(type ? {type} : {}),
  });
  const typesAreStages = Boolean(findChannelObj(channel.value)?.typesAreStages);
  // A server from before the shown marks would take a box that puts a
  // product on a stage as an error: those boxes need a server that knows them.
  const canAddToStage = typesAreStages && settings?.shownStageProducts !== undefined;
  let hiddenCount = 0;
  let addedCount = 0;
  // Releases has stages; the other channels have content types.
  const noun = typesAreStages ? "stage" : "type";

  // The stage itself, for every product.
  const stageRow = {
    key: "every-product",
    label: <span title={`Hide a ${noun} for every product`}>Every product</span>,
    cells: [
      null,
      ...stages.map((stage) => {
        const box = checkbox(
          productChange(ALL_PRODUCTS_SCOPE, stage.value),
          isHiddenOn(stage.value, ALL_PRODUCTS_SCOPE),
          {label: `${stage.label} for every product`, title: `${stage.label} for every product`}
        );
        if (box.isHidden) hiddenCount += 1;
        return {key: stage.value, ...box};
      }),
    ],
  };

  const productRows = products.map((product) => {
    const everywhere = checkbox(productChange(product.value), hiddenEverywhere.has(product.value), {
      label: `${product.label} on every ${noun}`,
      title: `Every ${noun}`,
    });
    if (everywhere.isHidden) hiddenCount += 1;
    // "All Products" (the What's New view of Releases) is on no stage list.
    const isSynthetic = product.value === ALL_PRODUCT.value;
    return {
      key: product.value,
      label: <span title={product.label}>{product.label}</span>,
      cells: [
        {key: "all", ...everywhere},
        ...stages.map((stage) => {
          if (isSynthetic) return null;
          const isInCode = isOnStageInCode(channel.value, stage.value, product.value);
          if (!isInCode && !canAddToStage) return null;
          const isStageHidden = isHiddenOn(stage.value, ALL_PRODUCTS_SCOPE);
          const isOff = everywhere.isHidden || isStageHidden;
          // Off: the product is hidden on the stage, or not on it.
          const isOffHere = isInCode
            ? isHiddenOn(stage.value, product.value)
            : !stageHasProduct(settings, channel.value, stage.value, product.value);
          const box = checkbox(productChange(product.value, stage.value), isOff || isOffHere, {
            label: `${product.label} on ${stage.label}`,
            disabled: isOff,
            title: everywhere.isHidden
              ? `Hidden on every ${noun}`
              : isStageHidden
                ? `${stage.label} is hidden for every product`
                : isInCode
                  ? stage.label
                  : addedBoxTitle(product.label, stage.label, isOffHere),
          });
          if (!isOff && isInCode && box.isHidden) hiddenCount += 1;
          if (!isOff && !isInCode && !box.isHidden) addedCount += 1;
          // A rule of its own: the box differs from the code.
          return {key: stage.value, isOwn: !isOff && box.isHidden === isInCode, ...box};
        }),
      ],
    };
  });

  const summary = [
    hiddenCount > 0 ? `${hiddenCount} hidden` : `all ${products.length} shown`,
    ...(addedCount > 0 ? [`${addedCount} added`] : []),
  ].join(", ");

  return (
    <CheckboxGroup
      title={`Products of ${channel.label}`}
      summary={summary}
      isChanged={hiddenCount > 0 || addedCount > 0}
      className="stage-visibility product-stage-visibility"
    >
      <Matrix
        columns={[
          {key: "all", label: `All ${noun}s`, title: `Every ${noun}`},
          ...stages.map((stage) => ({
            key: stage.value,
            label: stage.label,
            isCurrent: stage.value === currentType,
          })),
        ]}
        rows={[stageRow, ...productRows]}
      />
    </CheckboxGroup>
  );
}

export function SetStageVisibilityGroup({
  product,
  stages,
  currentType,
  settings,
  checkbox,
}: {
  product: Option;
  /** The stages of the releases channel, in the saved order. */
  stages: Option[];
  currentType: string | undefined;
  /** The settings of the releases channel. */
  settings: ChannelSettingsLike;
  checkbox: OptionCheckbox;
}) {
  // "All Products" has no stage lists of its own: its rules are the ones for
  // every product.
  const canScopeProduct = product.value !== ALL_PRODUCT.value;
  const [isEveryProduct, setIsEveryProduct] = useState(!canScopeProduct);
  const scope = isEveryProduct || !canScopeProduct ? ALL_PRODUCTS_SCOPE : product.value;
  const sets: Option[] =
    scope === ALL_PRODUCTS_SCOPE ? RELEASES_SETS : releasesSetsForProduct(product.value);
  const rowStages =
    scope === ALL_PRODUCTS_SCOPE
      ? stages
      : stages.filter((stage) => stageHasProduct(settings, "releases", stage.value, product.value));

  const hiddenEverywhere = new Set(settings?.hiddenSets ?? []);
  // A server from before the shown marks keeps a set hidden that a wider
  // rule hides: its box stays disabled.
  const canShowBelow = settings?.shownStageSets !== undefined;
  const setChange = (value: string, type?: string): Omit<HiddenChange, "hidden"> => ({
    channel: "releases",
    kind: "set",
    value,
    ...(type ? {type, product: scope} : {}),
  });
  let hiddenHere = 0;

  // The channel-wide list: every stage and every product.
  const everyStageRow = {
    key: "every-stage",
    label: <span title="Every stage, every product">All stages</span>,
    cells: sets.map((set) => {
      const box = checkbox(setChange(set.value), hiddenEverywhere.has(set.value), {
        label: `${set.label} on every stage`,
        title: `${set.label} on every stage`,
      });
      return {key: set.value, ...box};
    }),
  };

  // The rule above a cell: the "All stages" row for the rows of every
  // product, the stage for every product for the rows of one product.
  const ruleAbove = scope === ALL_PRODUCTS_SCOPE ? "every stage" : "every product";
  const stageRows = rowStages.map((stage) => {
    const hidden = hiddenSetsOf(settings, stage.value, scope);
    return {
      key: stage.value,
      label: stage.label,
      isCurrent: stage.value === currentType,
      cells: sets.map((set) => {
        const isHidden = hidden.has(set.value);
        const isHiddenAbove = isSetHiddenAbove(settings, stage.value, scope, set.value);
        const isLocked = isHiddenAbove && !canShowBelow;
        const box = checkbox(setChange(set.value, stage.value), isHidden, {
          label: `${set.label} on ${stage.label}`,
          disabled: isLocked,
          title: isLocked
            ? `Hidden for ${ruleAbove}`
            : isHidden !== isHiddenAbove
              ? `${isHidden ? "Hidden" : "Shown"} here, ${isHiddenAbove ? "hidden" : "shown"} for ${ruleAbove}`
              : isHidden
                ? `Hidden for ${ruleAbove}. Click to show it here`
                : `${set.label} on ${stage.label}`,
        });
        if (stage.value === currentType && box.isHidden) hiddenHere += 1;
        // A rule of its own: the cell differs from the rule above.
        return {key: set.value, isOwn: box.isHidden !== isHiddenAbove, ...box};
      }),
    };
  });

  return (
    <CheckboxGroup
      title="Sets"
      summary={hiddenHere > 0 ? `${hiddenHere} of ${sets.length} hidden here` : "all shown here"}
      isChanged={hiddenHere > 0 || hiddenEverywhere.size > 0}
      className="stage-visibility set-stage-visibility"
    >
      {canScopeProduct && (
        <div className="layout-visibility-scope" role="radiogroup" aria-label="Set rule scope">
          <label>
            <input type="radio" checked={!isEveryProduct} onChange={() => setIsEveryProduct(false)} />
            {product.label}
          </label>
          <label>
            <input type="radio" checked={isEveryProduct} onChange={() => setIsEveryProduct(true)} />
            All products
          </label>
        </div>
      )}
      <Matrix
        columns={sets.map((set) => ({key: set.value, label: set.label}))}
        rows={scope === ALL_PRODUCTS_SCOPE ? [everyStageRow, ...stageRows] : stageRows}
      />
    </CheckboxGroup>
  );
}
